import { useState } from "react";
import { Globe, Workflow, MonitorSmartphone, Trash2, Play, RefreshCw, Power } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { TID } from "../lib/testIds";

const TYPE_META = {
  browser: { label: "Navegador (Playwright)", Icon: MonitorSmartphone },
  rest_api: { label: "API REST", Icon: Globe },
  n8n_webhook: { label: "Webhook n8n", Icon: Workflow },
};

const STATE_META = {
  connected: { label: "Conectado", dot: "bg-emerald-500", text: "text-emerald-400" },
  disconnected: { label: "Desconectado", dot: "bg-zinc-500", text: "text-zinc-400" },
  error: { label: "Error", dot: "bg-red-500", text: "text-red-400" },
  not_implemented: {
    label: "No implementado",
    dot: "bg-amber-500",
    text: "text-amber-400",
  },
};

export default function ConnectionCard({ conn, onChange, onDeleted }) {
  const [busy, setBusy] = useState(null);
  const type = TYPE_META[conn.type] || TYPE_META.browser;
  const state = STATE_META[conn.state] || STATE_META.disconnected;
  const Icon = type.Icon;

  async function run(action, endpoint, method = "post") {
    setBusy(action);
    try {
      const { data } = await api[method](`/connections/${conn.id}/${endpoint}`);
      onChange?.(data);
      toast.success("Acción completada");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function del() {
    if (!window.confirm(`¿Eliminar la conexión "${conn.name}"?`)) return;
    setBusy("delete");
    try {
      await api.delete(`/connections/${conn.id}`);
      onDeleted?.(conn.id);
      toast.success("Conexión eliminada");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      data-testid={TID.connections.card(conn.id)}
      className="rounded-md border border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden hover:border-zinc-700 transition-colors duration-150"
    >
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
              <Icon className="h-4 w-4 text-zinc-300" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{conn.name}</div>
              <div className="text-[11px] font-mono text-zinc-500">{type.label}</div>
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 text-[11px] font-medium ${state.text}`}>
          <span className={`krai-dot ${state.dot}`} />
          {state.label}
        </div>
      </div>

      <div className="px-5 pb-4 space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">URL</div>
        <a
          href={conn.url}
          target="_blank"
          rel="noreferrer"
          className="block font-mono text-xs text-zinc-300 hover:text-white truncate"
        >
          {conn.url}
        </a>
        {conn.notes && (
          <p className="text-xs text-zinc-500 line-clamp-3 pt-2">{conn.notes}</p>
        )}
      </div>

      <div className="mt-auto border-t border-zinc-800 p-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          data-testid={TID.connections.connect(conn.id)}
          disabled={busy !== null}
          onClick={() => run("connect", "connect")}
          className="bg-white text-black hover:bg-zinc-200 h-8"
        >
          <Play className="h-3.5 w-3.5 mr-1.5" />
          Conectar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.connections.test(conn.id)}
          disabled={busy !== null}
          onClick={() => run("test", "test")}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Probar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.connections.disconnect(conn.id)}
          disabled={busy !== null}
          onClick={() => run("disconnect", "disconnect")}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
        >
          <Power className="h-3.5 w-3.5 mr-1.5" />
          Desconectar
        </Button>
        <button
          data-testid={TID.connections.delete(conn.id)}
          onClick={del}
          disabled={busy !== null}
          className="ml-auto h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
